import React, { useEffect, useMemo, useState } from 'react';

const AppointmentProposalForm = ({
  customers = [],
  vehicles = [],
  quotes = [],
  onSubmit,
  onCancel,
  loading,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    customer_id: '',
    vehicle_id: '',
    quote_id: '',
    notes: '',
  });

  const [errors, setErrors] = useState({});
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [availableQuotes, setAvailableQuotes] = useState([]);

  const [slotBaseDate, setSlotBaseDate] = useState('');
  const [slotBaseTime, setSlotBaseTime] = useState('');
  const [proposedSlots, setProposedSlots] = useState([]); // {start,end,duration_minutes}

  useEffect(() => {
    if (formData.customer_id) {
      const v = vehicles.filter(v => v.customer_id === formData.customer_id);
      setAvailableVehicles(v);
      const q = quotes.filter(q => q.customer_id === formData.customer_id && q.status === 'accepted');
      setAvailableQuotes(q);
    } else {
      setAvailableVehicles([]);
      setAvailableQuotes([]);
    }
  }, [formData.customer_id, vehicles, quotes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (name === 'customer_id') {
      setFormData(prev => ({ ...prev, vehicle_id: '', quote_id: '' }));
    }
  };

  const addSlot = (minutes) => {
    if (!slotBaseDate || !slotBaseTime) return;
    const start = new Date(`${slotBaseDate}T${slotBaseTime}`);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + minutes);
    setProposedSlots(prev => [...prev, {
      start: start.toISOString(),
      end: end.toISOString(),
      duration_minutes: minutes,
    }]);
  };

  const removeSlotAt = (idx) => setProposedSlots(prev => prev.filter((_, i) => i !== idx));

  const validate = () => {
    const e = {};
    if (!formData.title.trim()) e.title = 'Le titre est obligatoire';
    if (!formData.customer_id) e.customer_id = 'Le client est obligatoire';
    if (proposedSlots.length === 0) e.slots = 'Ajoutez au moins un créneau';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    const appointment = {
      title: formData.title,
      customer_id: formData.customer_id,
      vehicle_id: formData.vehicle_id || null,
      quote_id: formData.quote_id || null,
      notes: formData.notes || null,
      // Pas de start/end ici: on ne planifie pas encore, on propose des slots
    };
    await onSubmit({ appointment, slots: proposedSlots });
  };

  return (
    <div className="appointment-form-overlay">
      <div className="appointment-form-container">
        <div className="appointment-form-header">
          <h2>Proposer un rendez-vous</h2>
          <button type="button" onClick={onCancel} className="close-btn" title="Fermer">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="appointment-form">
          <div className="form-section">
            <h3>Informations</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="title">Titre *</label>
                <input id="title" name="title" value={formData.title} onChange={handleChange} className={errors.title ? 'error' : ''} placeholder="Ex: Révision, Diagnostic..." />
                {errors.title && <span className="error-text">{errors.title}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="customer_id">Client *</label>
                <select id="customer_id" name="customer_id" value={formData.customer_id} onChange={handleChange} className={errors.customer_id ? 'error' : ''}>
                  <option value="">Sélectionner un client</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ''}</option>)}
                </select>
                {errors.customer_id && <span className="error-text">{errors.customer_id}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="vehicle_id">Véhicule</label>
                <select id="vehicle_id" name="vehicle_id" value={formData.vehicle_id} onChange={handleChange} disabled={!formData.customer_id}>
                  <option value="">Sélectionner un véhicule</option>
                  {availableVehicles.map(v => (
                    <option key={v.id} value={v.id}>{v.brand} {v.model} {v.license_plate ? `(${v.license_plate})` : ''}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="quote_id">Devis associé (optionnel)</label>
                <select id="quote_id" name="quote_id" value={formData.quote_id} onChange={handleChange} disabled={!formData.customer_id}>
                  <option value="">Aucun</option>
                  {availableQuotes.map(q => (
                    <option key={q.id} value={q.id}>{q.quote_number} - {Number(q.total||0).toFixed(2)}€</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea id="notes" name="notes" value={formData.notes} onChange={handleChange} rows={4} placeholder="Contexte, contraintes, etc." />
            </div>
          </div>

          <div className="form-section">
            <h3>Créneaux à proposer</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="slotBaseDate">Date de départ</label>
                <input type="date" id="slotBaseDate" value={slotBaseDate} onChange={e=>setSlotBaseDate(e.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="slotBaseTime">Heure de départ</label>
                <input type="time" id="slotBaseTime" value={slotBaseTime} onChange={e=>setSlotBaseTime(e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <button type="button" className="btn-secondary" onClick={()=>addSlot(30)}>+ 30 min</button>
              <button type="button" className="btn-secondary" onClick={()=>addSlot(60)}>+ 60 min</button>
              <button type="button" className="btn-secondary" onClick={()=>addSlot(90)}>+ 90 min</button>
            </div>
            {errors.slots && <span className="error-text">{errors.slots}</span>}
            {proposedSlots.length>0 && (
              <div className="slots-list" style={{ marginTop: 12 }}>
                {proposedSlots.map((s, idx) => (
                  <div key={idx} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', border:'1px solid #e5e7eb', borderRadius:8, padding:8, marginBottom:6 }}>
                    <div>
                      <strong>{new Date(s.start).toLocaleDateString('fr-FR')} </strong>
                      <span>{new Date(s.start).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})} → {new Date(s.end).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}</span>
                      <span style={{ marginLeft: 8, color:'#6b7280' }}>({s.duration_minutes} min)</span>
                    </div>
                    <button type="button" className="btn" onClick={()=>removeSlotAt(idx)}>Retirer</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn">Annuler</button>
            <button type="submit" disabled={loading} className="btn-primary">Proposer au client</button>
          </div>
        </form>
        <style jsx>{`
          .appointment-form-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }

          .appointment-form-container {
            background: white;
            border-radius: 12px;
            width: 100%;
            max-width: 800px;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          }

          .appointment-form-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 24px 24px 0 24px;
            border-bottom: 1px solid #e5e7eb;
            margin-bottom: 24px;
            position: sticky;
            top: 0;
            background: white;
            z-index: 10;
          }

          .appointment-form-header h2 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
            color: #1f2937;
          }

          .close-btn {
            background: none;
            border: none;
            color: #6b7280;
            cursor: pointer;
            padding: 8px;
            border-radius: 6px;
            transition: all 0.2s ease;
          }

          .close-btn:hover {
            background: #f3f4f6;
            color: #374151;
          }

          .appointment-form {
            padding: 0 24px 24px 24px;
          }

          .form-section {
            margin-bottom: 32px;
            padding-bottom: 24px;
            border-bottom: 1px solid #f3f4f6;
          }

          .form-section:last-of-type {
            border-bottom: none;
          }

          .form-section h3 {
            margin: 0 0 20px 0;
            font-size: 1.2rem;
            font-weight: 600;
            color: #1f2937;
            border-left: 4px solid #3b82f6;
            padding-left: 12px;
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin-bottom: 16px;
          }

          @media (max-width: 640px) {
            .form-row {
              grid-template-columns: 1fr;
            }
          }

          .form-group { position: relative; margin-bottom: 16px; }
          .form-group label { display: block; margin-bottom: 6px; font-weight: 500; color: #374151; font-size: 0.9rem; }
          .form-group input, .form-group select, .form-group textarea { width: 100%; padding: 12px 16px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.9rem; transition: all 0.2s ease; box-sizing: border-box; }
          .form-group input:focus, .form-group select:focus, .form-group textarea:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
          .form-group input.error, .form-group select.error, .form-group textarea.error { border-color: #dc2626; }
          .form-group textarea { resize: vertical; min-height: 100px; }

          .error-text { color: #dc2626; font-size: 0.8rem; margin-top: 4px; display: block; }
        `}</style>
      </div>
    </div>
  );
};

export default AppointmentProposalForm;
